import { useLanguage } from '@/contexts/LanguageContext';
import { languageNames, Language } from '@/i18n/translations';
import { Globe } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

const flags: Record<Language, string> = {
  tr: '🇹🇷', en: '🇬🇧', de: '🇦🇹', fr: '🇫🇷', ru: '🇷🇺', it: '🇮🇹', es: '🇪🇸', ar: '🇸🇦',
};

const LanguageSelector = () => {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/10 hover:bg-primary/20 hover:text-white transition-colors text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        aria-label="Select language"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Globe className="w-4 h-4 text-primary" />
        <span>{flags[lang]}</span>
        <span className="hidden sm:inline">{languageNames[lang]}</span>
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-2 bg-secondary border border-border rounded-xl shadow-md overflow-hidden z-50 min-w-[200px] animate-fade-in-up">
          {(Object.keys(languageNames) as Language[]).map((l) => (
            <button
              key={l}
              onClick={() => { setLang(l); setOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium hover:bg-primary/20 hover:text-white transition-colors ${lang === l ? 'bg-primary/15 text-primary font-bold' : 'text-secondary-foreground'}`}
            >
              <span className="text-xl">{flags[l]}</span>
              <span className="tracking-wide">{languageNames[l]}</span>
              <span className="ml-auto text-xs text-secondary-foreground/50 uppercase">{l}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
